import {
  useImage,
  useResponsiveImage
} from "./chunk-RDOW37CZ.esm.js";
import {
  clsx,
  dataAttr,
  getThumbDataURL,
  useDOMRef
} from "./chunk-QPGVD7NS.esm.js";

// src/use-image.ts
import { useCallback, useMemo } from "react";
function useImage2(props) {
  const {
    ref,
    as,
    src,
    className,
    classNames,
    loading,
    isBlurred,
    fallbackSrc,
    isLoading: isLoadingProp,
    disableSkeleton = !!fallbackSrc,
    removeWrapper = false,
    onError,
    onLoad,
    srcSet,
    sizes,
    crossOrigin,
    thumb,
    responsiveImages,
    ...otherProps
  } = props;
  const {
    showResponsiveImage,
    responsiveImageSourceConfig,
    responsiveImageDefaultUrl,
    responsiveImageInitUrl
  } = useResponsiveImage(responsiveImages);
  const domRef = useDOMRef(ref);
  const imageStatus = useImage({
    src: responsiveImageInitUrl || src,
    loading,
    onError,
    onLoad,
    ignoreFallback: false,
    srcSet,
    sizes,
    crossOrigin
  });
  const isImgLoaded = imageStatus === "loaded" && !isLoadingProp;
  const isLoading = imageStatus === "loading" || isLoadingProp;
  const isZoomed = props.isZoomed;
  const Component = as || "img";
  const { w } = useMemo(() => {
    return {
      w: props.width ? typeof props.width === "number" ? `${props.width}px` : props.width : "fit-content"
    };
  }, [props == null ? void 0 : props.width]);
  const showFallback = (!src || !isImgLoaded) && !!fallbackSrc;
  let hashThumb = "";
  if (typeof window !== "undefined" && !disableSkeleton) {
    hashThumb = getThumbDataURL(domRef, props);
  }
  const showThumb = !isImgLoaded && !disableSkeleton && !!hashThumb;
  const showSkeleton = isLoading && !disableSkeleton && !hashThumb;
  const baseStyles = clsx(className, classNames == null ? void 0 : classNames.img);
  const getImgProps = (props2 = {}) => {
    const imgStyles = clsx(baseStyles, props2 == null ? void 0 : props2.className);
    return {
      src: showResponsiveImage ? responsiveImageDefaultUrl : src,
      ref: domRef,
      "data-loaded": dataAttr(isImgLoaded),
      className: showSkeleton ? clsx(imgStyles, classNames == null ? void 0 : classNames.skeletonImg) : imgStyles,
      loading,
      srcSet,
      sizes,
      crossOrigin,
      ...otherProps
    };
  };
  const getWrapperProps = useCallback(() => {
    if (showSkeleton) {
      return {
        className: classNames == null ? void 0 : classNames.skeletonWrapper,
        style: {
          maxWidth: w
        }
      };
    }
    const fallbackStyle = showThumb ? {
      backgroundImage: `url(${hashThumb})`,
      backgroundSize: "cover"
    } : showFallback ? {
      backgroundImage: `url(${fallbackSrc})`
    } : {};
    return {
      className: classNames == null ? void 0 : classNames.wrapper,
      style: {
        ...fallbackStyle,
        maxWidth: w
      }
    };
  }, [showSkeleton, showFallback, showThumb, fallbackSrc, classNames == null ? void 0 : classNames.wrapper]);
  const getBlurredImgProps = useCallback(() => {
    return {
      src,
      "aria-hidden": dataAttr(true),
      className: classNames == null ? void 0 : classNames.blurredImg
    };
  }, [src, classNames == null ? void 0 : classNames.blurredImg]);
  const getPictureProps = useCallback(() => {
    return {
      className: classNames == null ? void 0 : classNames.picture
    };
  }, [src, classNames == null ? void 0 : classNames.picture]);
  return {
    Component,
    domRef,
    classNames,
    isBlurred,
    disableSkeleton,
    fallbackSrc,
    removeWrapper,
    isZoomed,
    isLoading,
    thumb,
    showResponsiveImage,
    responsiveImageSourceConfig,
    getImgProps,
    getPictureProps,
    getWrapperProps,
    getBlurredImgProps
  };
}

export {
  useImage2 as useImage
};
//# sourceMappingURL=chunk-WBMNQIIL.esm.js.map